import { useCallback, useEffect, useState } from 'react';

// Stores a list of product IDs the visitor has hearted. Non-sensitive UI state —
// localStorage is the correct browser storage tier here. See /app/CODE_REVIEW_NOTES.md §4.
const STORAGE_KEY = 'ww-wishlist';

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function useWishlist() {
  const [ids, setIds] = useState(() => readStorage());

  useEffect(() => {
    const sync = () => setIds(readStorage());
    window.addEventListener('storage', sync);
    window.addEventListener('wishlist-changed', sync);
    return () => {
      window.removeEventListener('storage', sync);
      window.removeEventListener('wishlist-changed', sync);
    };
  }, []);

  const persist = (next) => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    window.dispatchEvent(new Event('wishlist-changed'));
  };

  const has = useCallback((id) => ids.includes(id), [ids]);

  const toggle = useCallback(
    (id) => {
      const current = readStorage();
      const next = current.includes(id)
        ? current.filter((x) => x !== id)
        : [...current, id];
      persist(next);
      setIds(next);
      return next.includes(id);
    },
    []
  );

  const remove = useCallback((id) => {
    const next = readStorage().filter((x) => x !== id);
    persist(next);
    setIds(next);
  }, []);

  return { ids, has, toggle, remove, count: ids.length };
}
